import React, { useState, useRef } from "react";
import {
  StyleSheet,
  View,
  ScrollView,
  Image,
  Dimensions,
  ImageSourcePropType,
  TouchableOpacity,
  Text,
} from "react-native";
import { colors } from "src/assets";
import { UserStatus } from "src/domains";
import LocalText from "src/local";

const MAX_WIDTH = 720;

export type HeaderProps = {
  status?: UserStatus;
  images: ImageSourcePropType[];
};

export const Header: React.FC<HeaderProps> = ({ images, status }) => {
  const [page, setPage] = useState<number>(0);
  const scrollViewRef = useRef<ScrollView>(null);
  const movePage = (offset: number) => {
    scrollViewRef.current?.scrollTo({
      x:
        (page + offset) *
        (Dimensions.get("window").width > MAX_WIDTH
          ? MAX_WIDTH
          : Dimensions.get("window").width),
      y: 0,
      animated: true,
    });
  };
  return (
    <View style={styles.container}>
      <ScrollView
        ref={scrollViewRef}
        horizontal
        pagingEnabled
        scrollEventThrottle={16}
        showsHorizontalScrollIndicator={false}
        onScroll={(e) => {
          const tmp = Math.floor(
            e.nativeEvent.contentOffset.x /
              (Dimensions.get("window").width > MAX_WIDTH
                ? MAX_WIDTH
                : Dimensions.get("window").width)
          );
          if (page !== tmp && tmp >= 0 && tmp < images.length) {
            setPage(tmp);
          }
        }}
      >
        {images.map((image, index) => (
          <Image
            key={index}
            source={image}
            resizeMode={"cover"}
            style={styles.image}
          />
        ))}
      </ScrollView>
      {page !== 0 ? (
        <View style={styles.btnLeftArrow}>
          <TouchableOpacity
            style={styles.btnArrow}
            onPress={() => movePage(-1)}
          >
            <svg width="8.59" height="15.646" viewBox="0 0 8.59 15.646">
              <path
                d="M24.383,280.5a.621.621,0,0,1-.44-.182L16.6,272.971a.623.623,0,0,1,0-.881l7.056-7.056a.623.623,0,0,1,.881.881l-6.615,6.615,6.9,6.9a.623.623,0,0,1-.44,1.063Z"
                transform="translate(-16.416 -264.852)"
                fill="#ff3000"
              />
            </svg>
          </TouchableOpacity>
        </View>
      ) : null}
      {page < images.length - 1 ? (
        <View style={styles.btnRightArrow}>
          <TouchableOpacity style={styles.btnArrow} onPress={() => movePage(1)}>
            <svg width="8.59" height="15.646" viewBox="0 0 8.59 15.646">
              <path
                d="M17.039,280.5a.621.621,0,0,0,.44-.182l7.344-7.344a.623.623,0,0,0,0-.881l-7.055-7.056a.623.623,0,0,0-.881.881l6.615,6.615-6.9,6.9a.623.623,0,0,0,.44,1.063Z"
                transform="translate(-16.416 -264.852)"
                fill="#ff3000"
              />
            </svg>
          </TouchableOpacity>
        </View>
      ) : (
        <View />
      )}
      <View style={styles.paginationOuterContainer}>
        <View style={styles.paginationContainer}>
          <Text style={styles.paginationText}>
            {page + 1} / {images.length}
          </Text>
        </View>
      </View>
      {status === UserStatus.LOGOUT && (
        <View style={[styles.textContainer, styles.logoutTextContainer]}>
          <Text style={styles.text}>{LocalText.common.userStatusLogout}</Text>
        </View>
      )}
      {status === UserStatus.OFFERABLE && (
        <View style={[styles.textContainer, styles.offerableTextContainer]}>
          <Text style={styles.text}>
            {LocalText.common.userStatusOfferable}
          </Text>
        </View>
      )}
      {status === UserStatus.ACTIVE && (
        <View style={[styles.textContainer, styles.activeTextContainer]}>
          <Text style={styles.text}>{LocalText.common.userStatusActive}</Text>
        </View>
      )}
      {status === UserStatus.IN_CALL && (
        <View style={[styles.textContainer, styles.inCallTextContainer]}>
          <Text style={styles.text}>{LocalText.common.userStatusInCall}</Text>
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: colors.gray1,
    borderBottomColor: colors.gray1,
    borderBottomWidth: 4,
  },
  image: {
    height: Dimensions.get("window").height / 3,
    width:
      Dimensions.get("window").width > MAX_WIDTH
        ? MAX_WIDTH
        : Dimensions.get("window").width,
  },
  arrowsContainer: {
    flex: 1,
    flexDirection: "row",
    justifyContent: "space-between",
  },
  btnLeftArrow: {
    position: "absolute",
    left: 0,
    top: 0,
    bottom: 0,
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    paddingTop: 30,
  },
  btnRightArrow: {
    position: "absolute",
    right: 0,
    top: 0,
    bottom: 0,
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    paddingTop: 30,
  },
  btnArrow: {
    width: 32,
    justifyContent: "center",
    alignItems: "center",
    paddingBottom: 20,
  },
  paginationOuterContainer: {
    position: "absolute",
    bottom: 8,
    left: 0,
    right: 0,
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
  },
  paginationContainer: {
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 4,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
  },
  paginationText: {
    fontSize: 12,
    fontWeight: "600",
    color: colors.white,
  },
  textContainer: {
    position: "absolute",
    right: 0,
    bottom: 0,
    backgroundColor: colors.chokuGreen,
    paddingHorizontal: 10,
    paddingVertical: 6,
  },
  logoutTextContainer: {
    backgroundColor: colors.chokuGray6,
  },
  offerableTextContainer: {
    backgroundColor: colors.chokuGreen,
  },
  activeTextContainer: {
    backgroundColor: colors.orange,
  },
  inCallTextContainer: {
    backgroundColor: colors.chokuRed,
  },
  text: {
    fontSize: 12,
    fontWeight: "bold",
    color: colors.white,
  },
});
